import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useStore } from '@/store/useStore';
import { Users, Trophy, Ticket, ArrowLeft, Share2, ShoppingBag } from 'lucide-react';
import { RaffleStatus } from '@/types';
import type { UserTicket } from '@/types';
import { api } from '@/services/api';
import { toast } from '@/store/useToast';

const API_BASE = import.meta.env.VITE_API_URL?.replace('/api', '') || 'http://localhost:8000';

export const RaffleDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { currentRaffle, user, loading, fetchRaffleDetails, enterRaffle } = useStore();
  const [isEntering, setIsEntering] = useState(false);
  const [modalImage, setModalImage] = useState<string | null>(null);
  const [myTickets, setMyTickets] = useState<UserTicket[]>([]);
  const [selectedTicketId, setSelectedTicketId] = useState<number | null>(null);

  useEffect(() => {
    if (id) {
      fetchRaffleDetails(Number(id));
    }
  }, [id]);

  useEffect(() => {
    if (currentRaffle && user) {
      loadMatchingTickets(currentRaffle.entry_price);
    }
  }, [currentRaffle?.id, user?.telegram_id]);

  const loadMatchingTickets = async (entryPrice: number) => {
    try {
      const all = await api.getMyTickets(true); // unused only
      const matching = all.filter((t) => t.price_paid === entryPrice);
      setMyTickets(matching);
      if (matching.length > 0) setSelectedTicketId(matching[0].id);
    } catch {
      setMyTickets([]);
    }
  };

  const handleEnter = async () => {
    if (!id || !user || !selectedTicketId) return;

    setIsEntering(true);
    try {
      await enterRaffle(Number(id), selectedTicketId);
      toast.success('Вы успешно вошли в розыгрыш!');
      fetchRaffleDetails(Number(id));
      await loadMatchingTickets(currentRaffle!.entry_price);
    } catch (error: any) {
      const msg = error?.response?.data?.detail || 'Ошибка при входе в розыгрыш';
      toast.error(msg);
    } finally {
      setIsEntering(false);
    }
  };

  const handleShare = () => {
    if (!currentRaffle) return;

    const shareText = `🎰 *${currentRaffle.title}*\n\n` +
      `🎫 Билетов: ${currentRaffle.participants_count}${currentRaffle.max_participants ? `/${currentRaffle.max_participants}` : ''}\n` +
      `💰 Цена билета: ${currentRaffle.entry_price.toFixed(0)} ₽\n\n` +
      `Присоединяйтесь к розыгрышу!`;

    // Use Telegram's shareURL method for better integration
    if (window.Telegram?.WebApp) {
      const botUsername = import.meta.env.VITE_BOT_USERNAME;
      const startParam = `raffle_${currentRaffle.id}`;
      window.Telegram.WebApp.openTelegramLink(
        `https://t.me/share/url?url=https://t.me/${botUsername}?start=${startParam}&text=${encodeURIComponent(shareText)}`
      );
    } else {
      // Fallback
      const shareUrl = `https://t.me/share/url?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(shareText)}`;
      window.open(shareUrl, '_blank');
    }
  };

  if (!currentRaffle) {
    return <div>Loading...</div>;
  }

  return (
    <div className="pb-20">
      {/* Header */}
      <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
          >
            <ArrowLeft size={20} />
          </button>
          <h1 className="text-lg font-bold">Детали розыгрыша</h1>
        </div>
        <button
          onClick={handleShare}
          className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
        >
          <Share2 size={18} />
          <span className="text-sm font-medium">Поделиться</span>
        </button>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Photo */}
        {currentRaffle.photo_url && (
          <div className="w-full mb-6 rounded-xl overflow-hidden shadow-lg cursor-pointer" onClick={() => setModalImage(currentRaffle.photo_url!)}>
            <img
              src={currentRaffle.photo_url}
              alt={currentRaffle.title}
              className="w-full h-64 object-cover"
            />
          </div>
        )}

        {/* Title */}
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          {currentRaffle.title}
        </h2>

        {/* Prizes */}
        {currentRaffle.prizes && currentRaffle.prizes.length > 0 && (
          <div className="mb-6 bg-gradient-to-r from-purple-50 to-pink-50 dark:from-gray-800 dark:to-gray-700 rounded-lg p-4">
            <h3 className="text-lg font-bold mb-3 text-gray-900 dark:text-white">🎁 Призы</h3>
            <div className="space-y-3">
              {currentRaffle.prizes.map((prize) => (
                <div key={prize.place} className="flex items-center gap-3 bg-white dark:bg-gray-800 rounded-lg p-3">
                  <div className="flex-shrink-0 w-12 h-12 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold text-lg">
                    {prize.place}
                  </div>
                  {prize.photo_url && (
                    <img
                      src={prize.photo_url}
                      alt={prize.name}
                      className="w-16 h-16 object-cover rounded-lg cursor-pointer hover:opacity-80 transition-opacity"
                      onClick={(e) => {
                        e.stopPropagation();
                        setModalImage(prize.photo_url!);
                      }}
                    />
                  )}
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900 dark:text-white">{prize.name}</p>
                    <p className="text-xs text-gray-500">{prize.place} место</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {currentRaffle.description && (
          <p className="text-gray-600 dark:text-gray-300 mb-6">
            {currentRaffle.description}
          </p>
        )}

        {/* Stats Grid */}
        <div className="mb-6">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow">
            <div className="flex items-center gap-2 mb-2">
              <Users className="text-blue-500" size={20} />
              <div className="text-sm text-gray-500">Куплено билетов</div>
            </div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white">
              {currentRaffle.participants_count}
              {currentRaffle.max_participants && ` / ${currentRaffle.max_participants}`}
            </div>
          </div>
        </div>

        {/* Entry Section */}
        {currentRaffle.status === RaffleStatus.ACTIVE && (
          <div className="bg-gradient-to-br from-blue-50 to-purple-50 dark:from-gray-800 dark:to-gray-700 rounded-lg p-6 mb-6">
            <h3 className="text-lg font-bold mb-1">Участвовать в розыгрыше</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              Цена входа: <span className="font-semibold text-gray-800 dark:text-gray-200">{currentRaffle.entry_price.toFixed(0)}₽</span>
            </p>

            {myTickets.length === 0 ? (
              <div className="text-center py-4">
                <Ticket size={36} className="mx-auto text-gray-300 mb-2" />
                <p className="text-sm text-gray-500 mb-3">
                  Нет подходящих билетов (нужен билет на {currentRaffle.entry_price.toFixed(0)}₽)
                </p>
                <button
                  onClick={() => navigate('/tickets')}
                  className="flex items-center gap-2 mx-auto px-5 py-2 bg-telegram-button text-telegram-buttonText rounded-xl text-sm font-semibold"
                >
                  <ShoppingBag size={16} />
                  Купить билет
                </button>
              </div>
            ) : (
              <>
                <label className="block text-sm font-medium mb-2">
                  Выберите билет ({myTickets.length} доступно):
                </label>
                <div className="space-y-2 mb-4">
                  {myTickets.map((t) => (
                    <label
                      key={t.id}
                      className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-colors ${
                        selectedTicketId === t.id
                          ? 'border-telegram-button bg-blue-50 dark:bg-blue-900/20'
                          : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800'
                      }`}
                    >
                      <input
                        type="radio"
                        name="ticket"
                        value={t.id}
                        checked={selectedTicketId === t.id}
                        onChange={() => setSelectedTicketId(t.id)}
                        className="accent-telegram-button"
                      />
                      <img
                        src={`${API_BASE}/ticket-images/${t.image_filename}`}
                        alt={t.image_name}
                        className="w-10 h-10 rounded-lg object-cover"
                        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                      />
                      <span className="text-sm font-medium">{t.image_name}</span>
                      <span className="ml-auto text-sm text-gray-500">#{t.id}</span>
                    </label>
                  ))}
                </div>

                <button
                  onClick={handleEnter}
                  disabled={!selectedTicketId || isEntering}
                  className="w-full py-3 bg-telegram-button text-telegram-buttonText rounded-lg font-bold disabled:opacity-50"
                >
                  {isEntering ? 'Отправка...' : 'Участвовать'}
                </button>
              </>
            )}
          </div>
        )}

        {/* Winners */}
        {currentRaffle.status === RaffleStatus.COMPLETED && currentRaffle.winners && currentRaffle.winners.length > 0 && (
          <div className="bg-gradient-to-r from-yellow-50 to-orange-50 dark:from-gray-800 dark:to-gray-700 rounded-lg p-6 shadow mb-6">
            <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
              <Trophy className="text-yellow-500" size={24} />
              Победители
            </h3>
            <div className="space-y-3">
              {currentRaffle.winners.map((winner) => (
                <div key={winner.place} className="bg-white dark:bg-gray-800 rounded-lg p-4 flex items-center gap-3">
                  <div className="flex-shrink-0 w-12 h-12 bg-yellow-500 text-white rounded-full flex items-center justify-center font-bold text-lg">
                    {winner.place}
                  </div>
                  <div className="flex-1">
                    <div className="font-bold text-gray-900 dark:text-white">
                      {winner.first_name || winner.username || `User ${winner.user_id}`}
                    </div>
                    <div className="text-sm text-purple-600 dark:text-purple-400">
                      🎁 {winner.prize_name}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Participants */}
        {currentRaffle.participants && currentRaffle.participants.length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow">
            <h3 className="text-lg font-bold mb-4">
              Участники ({currentRaffle.participants.length})
            </h3>
            <div className="space-y-2">
              {currentRaffle.participants.map((participant) => (
                <div
                  key={participant.user_id}
                  className="flex justify-between items-center py-2 border-b border-gray-100 dark:border-gray-700"
                >
                  <div>
                    <div className="font-medium">
                      {participant.first_name || participant.username || 'User'}
                    </div>
                    <div className="text-sm text-gray-500">
                      {participant.tickets_count} билетов
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-bold">{participant.amount_paid} ₽</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Image Modal */}
      {modalImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
          onClick={() => setModalImage(null)}
        >
          <div className="relative max-w-4xl max-h-full">
            <img
              src={modalImage}
              alt="Full size"
              className="max-w-full max-h-[90vh] object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
            <button
              onClick={() => setModalImage(null)}
              className="absolute top-4 right-4 bg-white text-gray-800 rounded-full w-10 h-10 flex items-center justify-center hover:bg-gray-200 transition-colors"
            >
              ✕
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
